import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ActivityBuilderThemeContext } from 'approot/activity-builder/theme/activity-builder-theme';
import { FONT_CALIBRE_SCHOOL } from 'approot/shared/styles/typography.style';
import { FillInBlanksAnswer } from '../fill-in-blanks.constants';
import { FontSize, getFontSizes } from '../use-fill-in-blanks-font-sizes';

function getTextWidth(text: string | null, font: string, fontSize: number) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context && text) {
    context.font = `${fontSize}px ${font}`;
    const metrics = context?.measureText(text);
    return metrics.width;
  }
  return 0;
}

type Props = {
  answer: FillInBlanksAnswer;
  fontSize: FontSize | null;
  fontFamily: string | null;
};

export const useFillInBlanksEditorWidth = ({
  answer,
  fontSize,
  fontFamily,
}: Props) => {
  const [editorRef, setEditorRef] = useState<HTMLDivElement | null>(null);
  const [hasTextOverflowed, setHasTextOverflowed] = useState<boolean>(false);

  const { originalFontSize, theme } = useContext(ActivityBuilderThemeContext);

  // get fill in blanks font size otherwise fallback to theme
  const numericalFontSize = useMemo(() => {
    const props = getFontSizes(originalFontSize, fontFamily, fontSize);
    return props?.fontSize ?? theme.theming.font.medium.size;
  }, [originalFontSize, fontFamily, fontSize, theme]);

  const adjustFieldState = useCallback(
    (target: HTMLDivElement) => {
      const text = target.textContent;
      const font = fontFamily || FONT_CALIBRE_SCHOOL;
      const textWidth = getTextWidth(text, font, numericalFontSize);

      if (textWidth > answer.width) {
        setHasTextOverflowed(true);
      } else {
        setHasTextOverflowed(false);
      }
    },
    [answer.width, numericalFontSize, fontFamily]
  );

  useEffect(() => {
    const editor = editorRef;
    if (!editor) return;

    function onEditorInput(e: Event) {
      const target = e.target as HTMLDivElement;
      adjustFieldState(target);
    }

    if (editorRef) {
      adjustFieldState(editorRef);
    }

    editorRef.addEventListener('input', onEditorInput, false);

    return () => {
      if (editorRef) {
        editorRef.removeEventListener('input', onEditorInput, false);
      }
    };
    //eslint-disable-next-line
  }, [editorRef, answer.width]);

  return {
    hasTextOverflowed,
    editorRef,
    adjustFieldState,
    setEditorRef,
  };
};
